// PhotoCapture — multi-photo camera capture component for the PWA.
// Uses native <input type="file" accept="image/*" capture="environment"> so on
// mobile devices it opens the rear camera directly. On desktop it falls back
// to the standard file picker. Up to 6 photos per batch.
//
// Props:
//   - photos: File[]   (current selection)
//   - onChange: (File[]) => void
//   - testid?: string  (defaults to "photo-capture")
//   - disabled?: bool
//   - max?: number  (default 6)
import React, { useCallback, useRef, useState, useEffect } from "react";

export default function PhotoCapture({ photos, onChange, testid = "photo-capture", disabled, max = 6 }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [previews, setPreviews] = useState([]);

  // Build thumbnail previews from the selected Files.
  useEffect(() => {
    if (!photos || photos.length === 0) { setPreviews([]); return; }
    const urls = photos.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [photos]);

  const handleFiles = useCallback((files) => {
    if (!files || !files.length) return;
    const incoming = Array.from(files);
    const valid = incoming.filter((f) => {
      const isImg = f.type?.startsWith("image/") || /\.(jpe?g|png|webp)$/i.test(f.name || "");
      const ok = isImg && f.size <= 10 * 1024 * 1024;
      if (!ok) {
        // eslint-disable-next-line no-alert
        alert(`الملف ${f.name} غير صالح — يجب صورة JPEG/PNG/WebP أقل من 10 ميجا`);
      }
      return ok;
    });
    const merged = [...(photos || []), ...valid].slice(0, max);
    onChange(merged);
  }, [photos, onChange, max]);

  const removeAt = (idx) => {
    const next = (photos || []).filter((_, i) => i !== idx);
    onChange(next);
  };

  const triggerCamera = () => { if (!disabled) cameraRef.current?.click(); };
  const triggerGallery = () => { if (!disabled) galleryRef.current?.click(); };

  return (
    <div data-testid={testid}>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        disabled={disabled}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        data-testid={`${testid}-camera-input`}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/*"
        multiple
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        disabled={disabled}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        data-testid={`${testid}-gallery-input`}
      />

      <div style={{
        display: "flex", gap: 8, flexWrap: "wrap",
        padding: 14, borderRadius: 12,
        border: "2px dashed var(--gold-deep)",
        background: "rgba(212, 175, 55, 0.05)",
      }}>
        <button type="button" onClick={triggerCamera} disabled={disabled || (photos?.length || 0) >= max}
          className="btn btn-primary" data-testid={`${testid}-take-photo`}
          style={{ flex: 1, padding: "12px 14px", fontSize: 13.5, fontWeight: 800, minWidth: 180 }}>
          📷 التقاط بالكاميرا
        </button>
        <button type="button" onClick={triggerGallery} disabled={disabled || (photos?.length || 0) >= max}
          className="btn btn-soft" data-testid={`${testid}-pick-gallery`}
          style={{ flex: 1, padding: "12px 14px", fontSize: 13.5, fontWeight: 700, minWidth: 180 }}>
          🖼 اختيار من المعرض
        </button>
      </div>

      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 6, lineHeight: 1.7 }}>
        💡 على الجوال يفتح الكاميرا الخلفية مباشرةً. يمكنك التقاط حتى {max} صور (10 ميجا لكل صورة). أَلتق صفحات التقرير الورقي بإضاءة جيدة وزاوية مستقيمة.
      </div>

      {previews.length > 0 && (
        <div style={{
          marginTop: 12,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
          gap: 8,
        }} data-testid={`${testid}-thumbs`}>
          {previews.map((url, idx) => (
            <div key={idx} style={{ position: "relative", borderRadius: 10, overflow: "hidden", border: "1px solid var(--border)", aspectRatio: "4/3" }}>
              <img src={url} alt={`photo-${idx}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <button type="button" onClick={() => removeAt(idx)}
                data-testid={`${testid}-remove-${idx}`}
                style={{
                  position: "absolute", top: 4, insetInlineEnd: 4,
                  background: "rgba(220,38,38,.92)", color: "#fff",
                  border: "none", borderRadius: "50%", width: 26, height: 26,
                  fontSize: 14, cursor: "pointer", fontWeight: 800,
                }} aria-label="حذف الصورة">×</button>
              <div style={{
                position: "absolute", bottom: 0, insetInlineStart: 0,
                background: "rgba(0,0,0,.55)", color: "#fff",
                fontSize: 10, padding: "2px 6px", borderTopEndRadius: 6,
              }}>{idx + 1} / {previews.length}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
